import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { TASK_STATUSES, PRIORITIES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TaskDialog({ open, onOpenChange, initial, projects = [], employees = [], lockedProject, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setF({
        titre: "", description: "", project_id: lockedProject?.id || "", project_name: lockedProject?.nom || "",
        responsable_id: "", responsable_name: "", priorite: "Moyenne", statut: "À faire",
        date_debut: "", date_echeance: "", duree_estimee: 0, avancement: 0, ...(initial || {}),
      });
    }
  }, [open, initial]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (f.id) await api.put(`/tasks/${f.id}`, f);
      else await api.post("/tasks", f);
      toast.success("Tâche enregistrée"); onOpenChange(false); onSaved && onSaved();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">{f.id ? "Modifier la tâche" : "Nouvelle tâche"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label className="text-xs">Titre</Label><Input value={f.titre || ""} onChange={(e) => set("titre", e.target.value)} required data-testid="task-titre" className="mt-1" /></div>
          <div className="md:col-span-2"><Label className="text-xs">Description</Label><Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1" /></div>
          {!lockedProject && (
            <div><Label className="text-xs">Projet</Label>
              <Select value={f.project_id || ""} onValueChange={(v) => { const p = projects.find((x) => x.id === v); set("project_id", v); set("project_name", p?.nom || ""); }}>
                <SelectTrigger className="mt-1" data-testid="task-project"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs">Responsable</Label>
            <Select value={f.responsable_id || ""} onValueChange={(v) => { const e = employees.find((x) => (x.user_id || x.id) === v); set("responsable_id", v); set("responsable_name", e ? `${e.prenom} ${e.nom}` : ""); }}>
              <SelectTrigger className="mt-1" data-testid="task-responsable"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.user_id || e.id}>{e.prenom} {e.nom}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Priorité</Label>
            <Select value={f.priorite} onValueChange={(v) => set("priorite", v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Statut</Label>
            <Select value={f.statut} onValueChange={(v) => set("statut", v)}><SelectTrigger className="mt-1" data-testid="task-statut"><SelectValue /></SelectTrigger>
              <SelectContent>{TASK_STATUSES.concat(["Annulée"]).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Date de début</Label><Input type="date" value={f.date_debut || ""} onChange={(e) => set("date_debut", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Date d'échéance</Label><Input type="date" value={f.date_echeance || ""} onChange={(e) => set("date_echeance", e.target.value)} className="mt-1" /></div>
          <div><Label className="text-xs">Durée estimée (h)</Label><Input type="number" value={f.duree_estimee || 0} onChange={(e) => set("duree_estimee", Number(e.target.value))} className="mt-1" /></div>
          <div><Label className="text-xs">Avancement (%)</Label><Input type="number" value={f.avancement || 0} onChange={(e) => set("avancement", Number(e.target.value))} className="mt-1" /></div>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="task-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
